import type { ActionTree } from "vuex";
import type { Realtime } from "ably";
import type { SpacePresenceState } from "./state";
import type { CursorWithDrawingData, MousePositionEventData, PresenceData } from "@/models";
import { RealtimeSpace } from "@/ws/realtime-space";
import { defineVuexName, MODULES, vuexName } from "../utils";

interface InitSpacePresencePayload {
  getRealtime: () => Realtime | undefined;
  profileData: PresenceData;
  enableSingleTab?: boolean;
  onKickOut?: () => void;
}

interface UpdateSpacePayload {
  channelName?: string;
  force?: boolean;
}

interface RegisterHandlersPayload {
  onCursor?: (payload: MousePositionEventData, senderId?: string) => void;
  onDrawing?: (payload: CursorWithDrawingData, senderId?: string) => void;
}

const actions: ActionTree<SpacePresenceState, any> = {
  async [defineVuexName(MODULES.SPACE_PRESENCE.DISPATCHES.INIT)]({ state, commit }, payload: InitSpacePresencePayload) {
    if (state.instance) {
      commit(vuexName(MODULES.SPACE_PRESENCE.COMMITS.SET_PROFILE_DATA, false), payload.profileData);
      return state.instance;
    }

    const instance = new RealtimeSpace({
      getRealtime: payload.getRealtime,
      presenceData: payload.profileData as unknown as Record<string, unknown>,
      onMembersUpdate: (members) => commit(vuexName(MODULES.SPACE_PRESENCE.COMMITS.SET_MEMBERS, false), members),
      enableSingleTab: payload.enableSingleTab,
      onKickOut: payload.onKickOut,
    });

    commit(vuexName(MODULES.SPACE_PRESENCE.COMMITS.SET_INSTANCE, false), instance);
    commit(vuexName(MODULES.SPACE_PRESENCE.COMMITS.SET_PROFILE_DATA, false), payload.profileData);
    return instance;
  },

  async [defineVuexName(MODULES.SPACE_PRESENCE.DISPATCHES.UPDATE_SPACE)]({ state, commit }, payload?: UpdateSpacePayload | string) {
    const instance = state.instance;
    if (!instance) {
      return;
    }

    const updatePayload: UpdateSpacePayload = typeof payload === "string" || payload === undefined ? { channelName: payload } : payload;

    const { channelName, force } = updatePayload;

    await instance.updateSpace(channelName, force);

    if (channelName) {
      const spaceChannelName = `space-${channelName}`;
      commit(vuexName(MODULES.SPACE_PRESENCE.COMMITS.SET_CHANNEL_NAME, false), spaceChannelName);
    }
  },

  async [defineVuexName(MODULES.SPACE_PRESENCE.DISPATCHES.REGISTER_HANDLERS)]({ state }, payload: RegisterHandlersPayload) {
    const instance = state.instance;
    if (!instance) {
      return () => undefined;
    }

    const unregisterFns: Array<() => void> = [];

    if (payload.onCursor) {
      unregisterFns.push(instance.addCursorListener(payload.onCursor));
    }

    if (payload.onDrawing) {
      unregisterFns.push(instance.addDrawingListener(payload.onDrawing));
    }

    return () => unregisterFns.forEach((fn) => fn());
  },

  async [defineVuexName(MODULES.SPACE_PRESENCE.DISPATCHES.SEND_CURSOR)]({ state }, payload: MousePositionEventData) {
    if (!state.instance) return;
    await state.instance.sendCursor(payload);
  },

  async [defineVuexName(MODULES.SPACE_PRESENCE.DISPATCHES.SEND_LASER)]({ state }, payload: CursorWithDrawingData) {
    if (!state.instance) return;
    await state.instance.sendLaser(payload);
  },

  async [defineVuexName(MODULES.SPACE_PRESENCE.DISPATCHES.SEND_PENCIL)]({ state }, payload: CursorWithDrawingData) {
    if (!state.instance) return;
    await state.instance.sendPencil(payload);
  },

  async [defineVuexName(MODULES.SPACE_PRESENCE.DISPATCHES.DISCONNECT)]({ state, commit }) {
    if (state.instance) {
      await state.instance.disconnect();
    }
    commit(vuexName(MODULES.SPACE_PRESENCE.COMMITS.RESET_STATE, false));
  },
};

export default actions;
